// Deterministic replays for Lesson 4.15 ("Agents That Work While Nobody Watches"). The two
// runs below were recorded on 9 October 2026 with zai.glm-5 through the gateway. The morning
// digest: three calls, 10,217 prompt tokens, nothing proposed, status done, the notice carrying
// the digest. The assignment command: four calls, 9,469 prompt and 253 completion tokens and
// five proposals waiting with the full budget, and the stop after two steps with 4,101 prompt
// tokens. The cost cap, the clock, the triggers and the coding trace are shaped on those
// numbers. Nothing here calls a model.

export type Tone = 'neutral' | 'success' | 'warning';
export interface Summary {
  message: string;
  tone: Tone;
}
const tokens = (value: number) => value.toLocaleString('en-US');

// --- the two recorded runs ------------------------------------------------------------------

export const RUN_IDS = ['digest', 'assign'] as const;
export type RunId = (typeof RUN_IDS)[number];
export const runLabels: Record<RunId, string> = {
  digest: 'the digest',
  assign: 'the assignment command',
};
export const toRunId = (value: string): RunId => RUN_IDS.find((id) => id === value) ?? 'digest';
export const ASSIGN_COMMAND = 'Assign every open Apollo task that has no owner to Jo.';
/** Prompt tokens after each call of the assignment command: 4,101 after two and 9,469 after
 * four were recorded, the other two are split from them. */
export const CUMULATIVE_PROMPT_TOKENS = [1_950, 4_101, 6_600, 9_469] as const;
/** Prompt tokens after each call of the digest: only the total, 10,217 after three, was
 * recorded; the first two are split from it. */
export const DIGEST_CUMULATIVE_PROMPT_TOKENS = [2_600, 5_900, 10_217] as const;
export const cumulativePromptTokens: Record<RunId, readonly number[]> = {
  digest: DIGEST_CUMULATIVE_PROMPT_TOKENS,
  assign: CUMULATIVE_PROMPT_TOKENS,
};
export const COMPLETION_TOKENS = 253;
export const PROPOSED_TASKS = 'tasks 10, 12, 13, 14, 15';
export const HONEST_SENTENCE =
  'I stopped after 2 steps without an answer. Here is what I looked at.';
export const promptTokensAfter = (step: number, run: RunId = 'assign') => {
  const totals = cumulativePromptTokens[run];
  return totals[Math.min(step, totals.length) - 1] ?? 0;
};

// --- unattended-run: a run is a job ------------------------------------------------------------

export interface RunStep {
  what: string;
  result: string;
}
const step = (what: string, result: string): RunStep => ({ what, result });
export const runSteps: Record<RunId, readonly RunStep[]> = {
  digest: [
    step('list the open Apollo tasks', '6 open, 5 with no owner'),
    step('search the notes for the week', "the week's notes with piece ids"),
    step('write the digest text', 'in the notice, nothing proposed'),
  ],
  assign: [
    step('list the projects', 'Apollo is project 1'),
    step('list the tasks', '6 open, 5 with no owner'),
    step('list the members', 'Jo is a member'),
    step('propose five assignments to Jo', `${PROPOSED_TASKS}, pending`),
  ],
};
export type RunStatus = 'queued' | 'running' | 'failed' | 'waiting' | 'done';
/** How each recorded run ended: the digest proposed nothing, the command waits for a person. */
export const finalStatus: Record<RunId, RunStatus> = { digest: 'done', assign: 'waiting' };
export interface RunEvent {
  row: string;
  status: RunStatus;
  promptTokens: number;
  kind: 'queued' | 'step' | 'crash' | 'resume' | 'final';
  /** True on the step a second worker ran after replaying the rows. */
  resumed: boolean;
}
export const CRASH_AFTER = 2;
export const QUEUED_EVENT: RunEvent = {
  row: 'queued by the trigger with its key',
  status: 'queued',
  promptTokens: 0,
  kind: 'queued',
  resumed: false,
};
/** Every event the record will show, in order: the crash and the resume sit before step 3. */
export function runSequence(run: RunId, crash: boolean): readonly RunEvent[] {
  const steps = runSteps[run];
  const events: RunEvent[] = [];
  steps.forEach((entry, index) => {
    const number = index + 1;
    const last = number === steps.length;
    if (crash && number === CRASH_AFTER + 1)
      events.push({
        row: `the worker died after storing step ${CRASH_AFTER}'s rows`,
        status: 'failed',
        promptTokens: promptTokensAfter(CRASH_AFTER, run),
        kind: 'crash',
        resumed: false,
      });
    const resumed = crash && number === CRASH_AFTER + 1;
    events.push({
      row: `${resumed ? 'a second worker replayed the rows, then ' : ''}step ${number}: ${entry.what}, ${entry.result}`,
      status: last ? finalStatus[run] : 'running',
      promptTokens: promptTokensAfter(number, run),
      kind: last ? 'final' : resumed ? 'resume' : 'step',
      resumed,
    });
  });
  return events;
}
export interface RunState {
  run: RunId;
  crash: boolean;
  /** How many events have been advanced into the record. */
  advanced: number;
}
export const initialRun = (): RunState => ({ run: 'digest', crash: false, advanced: 0 });
export type RunAction =
  | { type: 'run'; value: RunId }
  | { type: 'toggle-crash' }
  | { type: 'advance' }
  | { type: 'reset' };
export const runDone = (state: RunState) =>
  state.advanced >= runSequence(state.run, state.crash).length;
export function runTransition(state: RunState, action: RunAction): RunState {
  switch (action.type) {
    case 'reset':
      return initialRun();
    case 'run':
      return { ...state, run: action.value, advanced: 0 };
    case 'toggle-crash':
      return { ...state, crash: !state.crash, advanced: 0 };
    case 'advance':
      return runDone(state) ? state : { ...state, advanced: state.advanced + 1 };
  }
}
/** The record so far: the queued row, then one row per press. */
export const recordRows = (state: RunState): readonly RunEvent[] => [
  QUEUED_EVENT,
  ...runSequence(state.run, state.crash).slice(0, state.advanced),
];
export const currentEvent = (state: RunState): RunEvent => recordRows(state).at(-1) ?? QUEUED_EVENT;
export const stepsStored = (state: RunState) =>
  recordRows(state).filter((event) => event.kind !== 'queued' && event.kind !== 'crash').length;
export const RUN_START_MESSAGE = 'Choose a run, then press Advance.';
export const stepMessage = (number: number, promptTokens: number) =>
  `Step ${number} stored its rows, and the record shows ${tokens(promptTokens)} prompt tokens.`;
export const CRASH_MESSAGE = 'The worker died after step 2, and its stored rows remain.';
export const RESUME_MESSAGE =
  'A second worker replayed the rows and ran step 3, repeating nothing.';
export const DIGEST_DONE_MESSAGE =
  'The notice carries the digest, nothing is proposed, and the run is done.';
export const ASSIGN_WAITING_MESSAGE = 'Five proposals wait for a person, and no data changed.';
export const runFinalMessages: Record<RunId, string> = {
  digest: DIGEST_DONE_MESSAGE,
  assign: ASSIGN_WAITING_MESSAGE,
};
export const RUN_REFINEMENT =
  'Refinement: the record is the checkpoint, so a crashed run resumes from its rows.';
export function runSummary(state: RunState): Summary {
  if (state.advanced === 0) return { tone: 'neutral', message: RUN_START_MESSAGE };
  const event = currentEvent(state);
  switch (event.kind) {
    case 'crash':
      return { tone: 'warning', message: CRASH_MESSAGE };
    case 'resume':
      return { tone: 'success', message: RESUME_MESSAGE };
    case 'final':
      return {
        tone: 'success',
        message: event.resumed
          ? `${RESUME_MESSAGE} ${runFinalMessages[state.run]}`
          : runFinalMessages[state.run],
      };
    default:
      return { tone: 'neutral', message: stepMessage(stepsStored(state), event.promptTokens) };
  }
}

// --- unattended-limits: which limit stops the run ---------------------------------------------

export const LIMITS = ['budget', 'cost', 'time', 'none'] as const;
export type Limit = (typeof LIMITS)[number];
export const limitLabels: Record<Limit, string> = {
  budget: 'step budget 2',
  cost: 'cost cap 5,000 tokens',
  time: 'time limit 10 seconds',
  none: 'none',
};
export const toLimit = (value: string): Limit => LIMITS.find((id) => id === value) ?? 'budget';
export const COST_CAP = 5_000;
export const TIME_LIMIT_SECONDS = 10;
/** The clock is a model: about three seconds a step, since durations were not recorded. */
export const SECONDS_PER_STEP = 3;
export interface LimitStep {
  step: number;
  what: string;
  promptTokens: number;
  seconds: number;
  canceled: boolean;
}
const ASSIGN_STEPS = runSteps.assign;
export const limitSteps = (limit: Limit): readonly LimitStep[] => {
  const steps: LimitStep[] = [];
  for (let index = 0; index < ASSIGN_STEPS.length; index += 1) {
    const number = index + 1;
    const before = promptTokensAfter(index);
    const endsAt = number * SECONDS_PER_STEP;
    if (limit === 'budget' && number > 2) break;
    if (limit === 'cost' && before >= COST_CAP) break;
    const canceled = limit === 'time' && endsAt > TIME_LIMIT_SECONDS;
    steps.push({
      step: number,
      what: ASSIGN_STEPS[index]?.what ?? '',
      promptTokens: canceled ? before : promptTokensAfter(number),
      seconds: canceled ? TIME_LIMIT_SECONDS : endsAt,
      canceled,
    });
    if (canceled) break;
  }
  return steps;
};
export interface LimitOutcome {
  stoppedBy: string;
  status: RunStatus;
  /** The sentence the run left: recorded for the budget, built by code for the rest. */
  sentence: string;
  pending: string;
  promptTokens: number;
}
export const NOTHING_PENDING = 'nothing';
export const PENDING_PROPOSALS = `5 proposals, ${PROPOSED_TASKS}`;
export const NOTIFICATION =
  'It proposed 5 assignments to Jo (tasks 10, 12, 13, 14, 15) and they are waiting for your decision.';
export const limitOutcome = (limit: Limit): LimitOutcome => {
  const steps = limitSteps(limit);
  const completed = steps.filter((entry) => !entry.canceled);
  const promptTokens = completed.at(-1)?.promptTokens ?? 0;
  switch (limit) {
    case 'budget':
      return {
        stoppedBy: 'the step budget, after 2 steps',
        status: 'done',
        sentence: HONEST_SENTENCE,
        pending: NOTHING_PENDING,
        promptTokens,
      };
    case 'cost':
      return {
        stoppedBy: `the cost cap, before call ${completed.length + 1} at ${tokens(promptTokens)} tokens`,
        status: 'done',
        sentence: `Stopped by the cost cap at ${tokens(promptTokens)} tokens after ${completed.length} steps. Nothing was proposed.`,
        pending: NOTHING_PENDING,
        promptTokens,
      };
    case 'time':
      return {
        stoppedBy: `the time limit, at ${TIME_LIMIT_SECONDS} seconds during step ${steps.length}`,
        status: 'failed',
        sentence: `Stopped by the time limit at ${TIME_LIMIT_SECONDS} seconds during step ${steps.length}. Nothing was proposed.`,
        pending: NOTHING_PENDING,
        promptTokens,
      };
    case 'none':
      return {
        stoppedBy: 'nothing, the run is waiting for a person',
        status: 'waiting',
        sentence: NOTIFICATION,
        pending: PENDING_PROPOSALS,
        promptTokens,
      };
  }
};
export interface LimitsState {
  limit: Limit;
  ran: boolean;
}
export const initialLimits = (): LimitsState => ({ limit: 'budget', ran: false });
export type LimitsAction = { type: 'limit'; value: Limit } | { type: 'run' } | { type: 'reset' };
export function limitsTransition(state: LimitsState, action: LimitsAction): LimitsState {
  switch (action.type) {
    case 'reset':
      return initialLimits();
    case 'limit':
      return { limit: action.value, ran: false };
    case 'run':
      return { ...state, ran: true };
  }
}
export const LIMITS_START_MESSAGE = 'Choose a limit, then press Run the assignment command.';
export const BUDGET_MESSAGE = 'The budget stopped it after two reads, and nothing was proposed.';
export const COST_CAP_MESSAGE =
  'The cap stopped the fourth call at 6,600 tokens, so nothing was proposed.';
export const TIME_LIMIT_MESSAGE =
  'The deadline canceled step 4 at 10 seconds, so nothing was proposed.';
export const NO_LIMIT_MESSAGE =
  'Four calls, 9,469 prompt tokens and five proposals waiting for a person.';
export const LIMITS_REFINEMENT =
  'Refinement: a good stop leaves the data unchanged and the record complete.';
export function limitsSummary(state: LimitsState): Summary {
  if (!state.ran) return { tone: 'neutral', message: LIMITS_START_MESSAGE };
  switch (state.limit) {
    case 'budget':
      return { tone: 'neutral', message: BUDGET_MESSAGE };
    case 'cost':
      return { tone: 'neutral', message: COST_CAP_MESSAGE };
    case 'time':
      return { tone: 'warning', message: TIME_LIMIT_MESSAGE };
    case 'none':
      return { tone: 'success', message: NO_LIMIT_MESSAGE };
  }
}

// --- unattended-trigger: the same event twice ------------------------------------------------

export const TRIGGERS = ['schedule', 'webhook'] as const;
export type Trigger = (typeof TRIGGERS)[number];
export const triggerLabels: Record<Trigger, string> = {
  schedule: "the eight o'clock schedule",
  webhook: 'a new note webhook',
};
export const toTrigger = (value: string): Trigger =>
  TRIGGERS.find((id) => id === value) ?? 'schedule';
export const triggerKeys: Record<Trigger, string> = {
  schedule: 'digest:2026-10-09:apollo',
  webhook: 'note:evt_7f3a',
};
export interface Firing {
  firing: number;
  at: string;
  key: string;
  outcome: string;
}
const FIRING_TIMES: Record<Trigger, readonly [string, string]> = {
  schedule: ['08:00:00', '08:00:05, after the restart'],
  webhook: ['delivery 1', 'delivery 2, redelivered by design'],
};
export const NO_KEY = 'none';
export const RUN_CREATED = (number: number) => `run ${number} created`;
export const DUPLICATE_CAUGHT = 'found run 1 by its key, nothing created';
export const firings = (trigger: Trigger, key: boolean): readonly Firing[] =>
  FIRING_TIMES[trigger].map((at, index) => ({
    firing: index + 1,
    at,
    key: key ? triggerKeys[trigger] : NO_KEY,
    outcome: index === 0 || !key ? RUN_CREATED(index + 1) : DUPLICATE_CAUGHT,
  }));
export const runsCreated = (trigger: Trigger, key: boolean) =>
  firings(trigger, key).filter((firing) => firing.outcome !== DUPLICATE_CAUGHT).length;
export const duplicateCaught = (trigger: Trigger, key: boolean) =>
  firings(trigger, key).some((firing) => firing.outcome === DUPLICATE_CAUGHT) ? 'yes' : 'no';
export const wentOut = (trigger: Trigger, key: boolean) => {
  const runs = runsCreated(trigger, key);
  if (trigger === 'schedule') return runs === 1 ? '1 digest' : `${runs} digests`;
  return runs === 1 ? 'the note processed once' : `the note processed ${runs} times`;
};
export interface TriggerState {
  trigger: Trigger;
  key: boolean;
  fired: boolean;
}
export const initialTrigger = (): TriggerState => ({
  trigger: 'schedule',
  key: false,
  fired: false,
});
export type TriggerAction =
  | { type: 'trigger'; value: Trigger }
  | { type: 'toggle-key' }
  | { type: 'fire' }
  | { type: 'reset' };
export function triggerTransition(state: TriggerState, action: TriggerAction): TriggerState {
  switch (action.type) {
    case 'reset':
      return initialTrigger();
    case 'trigger':
      return { ...state, trigger: action.value, fired: false };
    case 'toggle-key':
      return { ...state, key: !state.key, fired: false };
    case 'fire':
      return { ...state, fired: true };
  }
}
export const TRIGGER_START_MESSAGE = 'Choose a trigger, then press Fire twice.';
export const SCHEDULE_TWICE_MESSAGE = 'Two runs started, so two digests went out.';
export const SCHEDULE_ONCE_MESSAGE =
  'The second firing found the first run by its key and stopped.';
export const WEBHOOK_TWICE_MESSAGE =
  'Both deliveries started a run, so the note was processed twice.';
export const WEBHOOK_ONCE_MESSAGE =
  "The second delivery matched the first run's key and created nothing.";
export const TRIGGER_REFINEMENT =
  'Refinement: the key from Lesson 4.8 on every trigger, as one unique index.';
export function triggerSummary(state: TriggerState): Summary {
  if (!state.fired) return { tone: 'neutral', message: TRIGGER_START_MESSAGE };
  if (state.trigger === 'schedule')
    return state.key
      ? { tone: 'success', message: SCHEDULE_ONCE_MESSAGE }
      : { tone: 'warning', message: SCHEDULE_TWICE_MESSAGE };
  return state.key
    ? { tone: 'success', message: WEBHOOK_ONCE_MESSAGE }
    : { tone: 'warning', message: WEBHOOK_TWICE_MESSAGE };
}

// --- unattended-coding: a coding agent's trace -----------------------------------------------

export const REQUESTS = ['test', 'rename'] as const;
export type Request = (typeof REQUESTS)[number];
export const requestLabels: Record<Request, string> = {
  test: 'add a test for the duplicate rule',
  rename: 'rename a function',
};
export const toRequest = (value: string): Request => REQUESTS.find((id) => id === value) ?? 'test';
export type ToolKind = 'file' | 'search' | 'edit' | 'permission' | 'shell' | 'answer';
export interface CodingStep {
  tool: ToolKind;
  what: string;
  result: string;
}
const coding = (tool: ToolKind, what: string, result: string): CodingStep => ({
  tool,
  what,
  result,
});
/** An illustration of the loop's shape, not a recorded run: the workspace agent's traces were
 * not recorded. */
export const codingTraces: Record<Request, readonly CodingStep[]> = {
  test: [
    coding('file', 'read inbox/rules.py', 'the rule that flags two notes with the same title'),
    coding(
      'search',
      'search the project for "duplicate"',
      '3 matches: inbox/rules.py, inbox/compare.py, tests/test_inbox.py',
    ),
    coding('file', 'read tests/test_inbox.py', '11 tests, none for the duplicate rule'),
    coding('edit', 'edit tests/test_inbox.py', 'added test_duplicate_rule_flags_same_title'),
    coding(
      'permission',
      'Run "pytest tests/test_inbox.py -q"? [y/n]',
      'the person at the terminal answered y',
    ),
    coding('shell', 'pytest tests/test_inbox.py -q', '12 passed'),
    coding('answer', 'the reply', 'Added one test for the duplicate rule, and the 12 tests pass.'),
  ],
  rename: [
    coding('search', 'search the project for "is_dupe"', '4 matches in 3 files'),
    coding('file', 'read inbox/rules.py', 'the definition of is_dupe'),
    coding('edit', 'edit inbox/rules.py', 'def is_duplicate, and the call inside the module'),
    coding('edit', 'edit inbox/compare.py and tests/test_inbox.py', 'two call sites renamed'),
    coding('permission', 'Run "pytest -q"? [y/n]', 'the person at the terminal answered y'),
    coding('shell', 'pytest -q', '24 passed'),
    coding(
      'answer',
      'the reply',
      'Renamed is_dupe to is_duplicate in three files, and the suite passes.',
    ),
  ],
};
export const toolsUsed = (steps: readonly CodingStep[]) =>
  Array.from(
    new Set(
      steps
        .filter((entry) => entry.tool !== 'permission' && entry.tool !== 'answer')
        .map((entry) => entry.tool),
    ),
  ).join(', ');
export const permissionPrompts = (steps: readonly CodingStep[]) =>
  steps.filter((entry) => entry.tool === 'permission').length;
export const filesChanged = (steps: readonly CodingStep[]) =>
  steps
    .filter((entry) => entry.tool === 'edit')
    .reduce((count, entry) => count + entry.what.split(' and ').length, 0);
export interface CodingState {
  request: Request;
  /** How many steps have been read from the top. */
  read: number;
}
export const initialCoding = (): CodingState => ({ request: 'test', read: 0 });
export type CodingAction =
  { type: 'request'; value: Request } | { type: 'read' } | { type: 'reset' };
export const codingDone = (state: CodingState) => state.read >= codingTraces[state.request].length;
export function codingTransition(state: CodingState, action: CodingAction): CodingState {
  switch (action.type) {
    case 'reset':
      return initialCoding();
    case 'request':
      return { request: action.value, read: 0 };
    case 'read':
      return codingDone(state) ? state : { ...state, read: state.read + 1 };
  }
}
export const revealedCodingSteps = (state: CodingState): readonly CodingStep[] =>
  codingTraces[state.request].slice(0, state.read);
export const READ_FROM_TOP = 'Read from the top';
export const READ_NEXT = 'Read the next step';
export const codingReadLabel = (state: CodingState) =>
  state.read === 0 ? READ_FROM_TOP : READ_NEXT;
export const CODING_START_MESSAGE = 'Choose a request, then press Read from the top.';
export const CODING_FINAL_MESSAGE =
  'The same loop, with tools that touch the file system and one approval.';
export const codingStepMessage = (number: number, tool: ToolKind) => {
  switch (tool) {
    case 'file':
      return `Step ${number} read a file, a tool call like search_notes.`;
    case 'search':
      return `Step ${number} searched the project, a read that changes nothing.`;
    case 'edit':
      return `Step ${number} edited a file, the first tool that touched the file system.`;
    case 'permission':
      return `Step ${number} paused for the person's permission before the shell command.`;
    case 'shell':
      return `Step ${number} ran the command in the workspace after the approval.`;
    case 'answer':
      return CODING_FINAL_MESSAGE;
  }
};
export const CODING_REFINEMENT =
  "Refinement: the permission prompt is Lesson 4.9's approval, with the person at the terminal.";
export function codingSummary(state: CodingState): Summary {
  if (state.read === 0) return { tone: 'neutral', message: CODING_START_MESSAGE };
  const current = codingTraces[state.request][state.read - 1];
  if (!current) return { tone: 'neutral', message: CODING_START_MESSAGE };
  if (current.tool === 'answer') return { tone: 'success', message: CODING_FINAL_MESSAGE };
  return {
    tone: current.tool === 'permission' ? 'warning' : 'neutral',
    message: codingStepMessage(state.read, current.tool),
  };
}
